import workerSrc from "url:pdfjs-dist/build/pdf.worker.min.mjs";

let pdfjsPromise = null;

// Loaded on first use so pdf.js stays out of the main bundle.
function loadPdfjs() {
  if (!pdfjsPromise) {
    pdfjsPromise = import("pdfjs-dist/build/pdf.mjs").then(
      function (pdfjsLib) {
        pdfjsLib.GlobalWorkerOptions.workerSrc = workerSrc;
        return pdfjsLib;
      },
      function (error) {
        // A cached rejection would fail every later document for the life of the tab.
        pdfjsPromise = null;
        throw error;
      },
    );
  }
  return pdfjsPromise;
}

const myPdfs = {};

function renderPdf(pdfs, canvas, pageno, scale, width) {
  const pdf = pdfs.pdf;
  pdf.getPage(pageno).then(function (page) {
    let viewport = page.getViewport({ scale: scale });

    if (width) {
      const wscale = width / viewport.width;
      const newScale = wscale * scale;
      const height = wscale * viewport.height;
      viewport = page.getViewport({ scale: newScale });
      viewport.width = width;
      viewport.height = height;
    }

    const context = canvas.getContext("2d", { alpha: false });
    const outputScale = getOutputScale(context);

    canvas.width = (viewport.width * outputScale.sx) | 0;
    canvas.height = (viewport.height * outputScale.sy) | 0;
    canvas.style.height = viewport.height + "px";
    canvas.style.width = viewport.width + "px";

    context.scale(outputScale.sx, outputScale.sy);

    pdfs.prevHeight = viewport.height;
    pdfs.prevWidth = viewport.width;

    const renderContext = {
      canvasContext: context,
      viewport: viewport,
    };
    page.render(renderContext);
  });
}

function getOutputScale(ctx) {
  const devicePixelRatio = window.devicePixelRatio || 1;
  const backingStoreRatio =
    ctx.webkitBackingStorePixelRatio ||
    ctx.mozBackingStorePixelRatio ||
    ctx.msBackingStorePixelRatio ||
    ctx.oBackingStorePixelRatio ||
    ctx.backingStorePixelRatio ||
    1;
  const pixelRatio = devicePixelRatio / backingStoreRatio;
  return {
    sx: pixelRatio,
    sy: pixelRatio,
    scaled: pixelRatio !== 1,
  };
}

class PdfViewerCustomElement extends HTMLElement {
  constructor() {
    super();
    const shadow = this.attachShadow({ mode: "open" });
    this.canvas = document.createElement("canvas");
    shadow.appendChild(this.canvas);
  }

  connectedCallback() {
    const name = this.getAttribute("name");
    const page = parseInt(this.getAttribute("page"), 10);
    const widthRaw = Number.parseFloat(this.getAttribute("width"));
    const width = Number.isFinite(widthRaw) ? widthRaw : undefined;

    const pdf = myPdfs[name];
    if (pdf) {
      if (width) {
        this.canvas.width = width;
      } else if (pdf.prevWidth) {
        this.canvas.width = pdf.prevWidth;
      }

      if (pdf.prevHeight) {
        this.canvas.height = pdf.prevHeight;
      }
      renderPdf(pdf, this.canvas, page, 1, width);
    }
  }
}

// iOS Safari caps total canvas memory; past it, canvases silently render blank.
const PDF_DOCUMENT_MAX_OUTPUT_SCALE = 2;
const PDF_DOCUMENT_PAGE_PADDING = 12;

const pdfDocumentTemplate = document.createElement("template");
pdfDocumentTemplate.innerHTML = `
  <style>
    :host { display: block; overflow-x: hidden; overflow-y: auto; -webkit-overflow-scrolling: touch; }
    [hidden] { display: none !important; }
    .pages { display: flex; flex-direction: column; align-items: center; gap: ${PDF_DOCUMENT_PAGE_PADDING}px; padding: ${PDF_DOCUMENT_PAGE_PADDING}px; }
    .page { position: relative; background: #fff; box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2); --scale-round-x: 1px; --scale-round-y: 1px; }
    .textLayer { position: absolute; inset: 0; overflow: clip; line-height: 1; text-align: initial; -webkit-text-size-adjust: none; text-size-adjust: none; forced-color-adjust: none; transform-origin: 0 0; caret-color: CanvasText; --min-font-size: 1; --text-scale-factor: calc(var(--total-scale-factor) * var(--min-font-size)); --min-font-size-inv: calc(1 / var(--min-font-size)); }
    .textLayer span, .textLayer br { color: transparent; position: absolute; white-space: pre; cursor: text; transform-origin: 0% 0%; -webkit-user-select: text; user-select: text; }
    .textLayer > :not(.markedContent), .textLayer .markedContent span:not(.markedContent) { z-index: 1; --font-height: 0; font-size: calc(var(--text-scale-factor) * var(--font-height)); --scale-x: 1; --rotate: 0deg; transform: rotate(var(--rotate)) scaleX(var(--scale-x)) scale(var(--min-font-size-inv)); }
    .textLayer .markedContent { display: contents; }
    .textLayer ::selection { background: rgba(0, 100, 255, 0.25); }
    .textLayer br::selection { background: transparent; }
    .textLayer .endOfContent { display: block; position: absolute; inset: 100% 0 0; z-index: 0; cursor: default; -webkit-user-select: none; user-select: none; }
    canvas { display: block; }
  </style>
  <slot name="loading"></slot>
  <slot name="error" hidden></slot>
  <div class="pages" hidden></div>
`;

// No iframe: iOS can't scroll a framed PDF, and one is blank wherever the
// browser's own PDF viewer is disabled.
class PdfDocumentCustomElement extends HTMLElement {
  static get observedAttributes() {
    return ["src"];
  }

  constructor() {
    super();
    const shadow = this.attachShadow({ mode: "open" });
    shadow.appendChild(pdfDocumentTemplate.content.cloneNode(true));
    this.loadingSlot = shadow.querySelector('slot[name="loading"]');
    this.errorSlot = shadow.querySelector('slot[name="error"]');
    this.pagesEl = shadow.querySelector(".pages");
    this.generation = 0;
    this.pages = [];
    this.layoutWidth = 0;
  }

  connectedCallback() {
    this.load();
  }

  disconnectedCallback() {
    this.teardown();
  }

  attributeChangedCallback(_name, oldValue, newValue) {
    if (this.isConnected && oldValue !== newValue) {
      this.load();
    }
  }

  setState(state) {
    this.loadingSlot.hidden = state !== "loading";
    this.errorSlot.hidden = state !== "error";
    this.pagesEl.hidden = state !== "ready";
  }

  load() {
    this.teardown();
    const url = this.getAttribute("src");
    if (!url) {
      return;
    }
    const generation = this.generation;
    this.setState("loading");

    loadPdfjs()
      .then((pdfjsLib) => {
        if (generation !== this.generation) {
          throw new Error("superseded while pdf.js was loading");
        }
        this.pdfjsLib = pdfjsLib;
        // The signed URL expires 30s after issue. A range request made later
        // would be refused, so fetch the whole file in one request up front.
        this.loadingTask = pdfjsLib.getDocument({ url: url, disableRange: true });
        return this.loadingTask.promise;
      })
      .then((pdf) => {
        const pageNumbers = Array.from({ length: pdf.numPages }, (_, i) => i + 1);
        return Promise.all(pageNumbers.map((n) => pdf.getPage(n)));
      })
      .then((pdfPages) => {
        if (generation !== this.generation) {
          return;
        }
        this.buildPages(pdfPages);
        this.setState("ready");
        this.layout();
      })
      .catch(() => {
        if (generation === this.generation) {
          this.setState("error");
        }
      });
  }

  buildPages(pdfPages) {
    this.pages = pdfPages.map((page) => {
      const pageEl = document.createElement("div");
      pageEl.className = "page";
      const canvas = document.createElement("canvas");
      canvas.setAttribute("aria-hidden", "true");
      pageEl.appendChild(canvas);
      // The canvas is invisible to screen readers, find-in-page and selection;
      // this transparent text layer carries the words. It stays mounted when
      // the canvas is released.
      const textLayerEl = document.createElement("div");
      textLayerEl.className = "textLayer";
      pageEl.appendChild(textLayerEl);
      const textLayer = new this.pdfjsLib.TextLayer({
        textContentSource: page.streamTextContent(),
        container: textLayerEl,
        viewport: page.getViewport({ scale: 1 }),
      });
      textLayer.render().catch(function () {});
      this.pagesEl.appendChild(pageEl);
      return { page, pageEl, canvas, textLayer, visible: false, renderTask: null, renderedWidth: 0 };
    });

    const entryFor = new Map(this.pages.map((entry) => [entry.pageEl, entry]));
    this.intersectionObserver = new IntersectionObserver(
      (observed) => {
        observed.forEach((o) => {
          const entry = entryFor.get(o.target);
          entry.visible = o.isIntersecting;
          if (entry.visible) {
            this.renderPage(entry);
          } else {
            this.releasePage(entry);
          }
        });
      },
      { root: this, rootMargin: "100% 0px" },
    );
    this.pages.forEach((entry) => this.intersectionObserver.observe(entry.pageEl));

    this.resizeObserver = new ResizeObserver(() => this.layout());
    this.resizeObserver.observe(this);
  }

  layout() {
    const width = Math.floor(this.clientWidth - 2 * PDF_DOCUMENT_PAGE_PADDING);
    if (width < 1 || width === this.layoutWidth) {
      return;
    }
    this.layoutWidth = width;
    this.pages.forEach((entry) => {
      const base = entry.page.getViewport({ scale: 1 });
      entry.pageEl.style.width = width + "px";
      entry.pageEl.style.height = Math.round((width * base.height) / base.width) + "px";
      entry.pageEl.style.setProperty("--total-scale-factor", String(width / base.width));
      if (entry.visible) {
        this.renderPage(entry);
      }
    });
  }

  renderPage(entry) {
    const width = this.layoutWidth;
    if (width < 1 || entry.renderedWidth === width) {
      return;
    }
    this.cancelRender(entry);

    const base = entry.page.getViewport({ scale: 1 });
    const viewport = entry.page.getViewport({ scale: width / base.width });
    const context = entry.canvas.getContext("2d", { alpha: false });
    const outputScale = Math.min(getOutputScale(context).sx, PDF_DOCUMENT_MAX_OUTPUT_SCALE);

    entry.canvas.width = Math.floor(viewport.width * outputScale);
    entry.canvas.height = Math.floor(viewport.height * outputScale);
    entry.canvas.style.width = viewport.width + "px";
    entry.canvas.style.height = viewport.height + "px";
    entry.renderedWidth = width;

    const renderTask = entry.page.render({
      canvas: entry.canvas,
      viewport: viewport,
      transform: outputScale !== 1 ? [outputScale, 0, 0, outputScale, 0, 0] : undefined,
    });
    entry.renderTask = renderTask;
    // A cancelled task rejects after its replacement has started; it must not
    // clear the replacement's state.
    renderTask.promise.then(
      () => {
        if (entry.renderTask === renderTask) {
          entry.renderTask = null;
        }
      },
      () => {
        if (entry.renderTask === renderTask) {
          entry.renderTask = null;
          entry.renderedWidth = 0;
        }
      },
    );
  }

  cancelRender(entry) {
    if (entry.renderTask) {
      entry.renderTask.cancel();
      entry.renderTask = null;
    }
  }

  // Zeroing the canvas frees its backing store (see the iOS memory cap above).
  releasePage(entry) {
    this.cancelRender(entry);
    entry.canvas.width = 0;
    entry.canvas.height = 0;
    entry.renderedWidth = 0;
  }

  teardown() {
    this.generation += 1;
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
      this.intersectionObserver = null;
    }
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
    this.pages.forEach((entry) => {
      this.cancelRender(entry);
      entry.textLayer.cancel();
    });
    this.pages = [];
    this.layoutWidth = 0;
    this.pagesEl.replaceChildren();
    if (this.loadingTask) {
      this.loadingTask.destroy().catch(function () {});
      this.loadingTask = null;
    }
  }
}

function formatPdfError(reason) {
  if (reason == null) {
    return "unknown error";
  }
  if (typeof reason === "string") {
    return reason;
  }
  if (typeof reason === "object" && reason.message) {
    return String(reason.message);
  }
  return String(reason);
}

function pdfCommandHandler(app, cmd, pdfjsLib) {
  if (!app.ports.receivePdfMsg) {
    return;
  }
  if (cmd.cmd === "openurl") {
    pdfjsLib.getDocument({ url: cmd.url }).promise.then(
      function (pdf) {
        myPdfs[cmd.name] = { pdf: pdf };
        app.ports.receivePdfMsg.send({
          msg: "loaded",
          name: cmd.name,
          pageCount: pdf.numPages,
        });
      },
      function (reason) {
        app.ports.receivePdfMsg.send({
          msg: "error",
          name: cmd.name,
          error: formatPdfError(reason),
        });
      },
    );
  } else if (cmd.cmd === "openstring") {
    const buff = Uint8Array.from(atob(cmd.string), (c) => c.charCodeAt(0));
    pdfjsLib.getDocument({ data: buff }).promise.then(
      function (pdf) {
        myPdfs[cmd.name] = { pdf: pdf };
        app.ports.receivePdfMsg.send({
          msg: "loaded",
          name: cmd.name,
          pageCount: pdf.numPages,
        });
      },
      function (reason) {
        app.ports.receivePdfMsg.send({
          msg: "error",
          name: cmd.name,
          error: formatPdfError(reason),
        });
      },
    );
  } else if (cmd.cmd === "close") {
    const entry = myPdfs[cmd.name];
    delete myPdfs[cmd.name];
    if (entry && entry.pdf) {
      entry.pdf.loadingTask.destroy().catch(function () {});
    }
  } else {
    app.ports.receivePdfMsg.send({
      msg: "error",
      name: typeof cmd.name === "string" ? cmd.name : "",
      error: "invalid command to pdf-element: " + cmd.cmd,
    });
  }
}

const PdfPreviewPort = {
  add: function (app) {
    if (typeof customElements !== "undefined" && !customElements.get("pdf-element")) {
      customElements.define("pdf-element", PdfViewerCustomElement);
    }
    if (typeof customElements !== "undefined" && !customElements.get("pdf-document")) {
      customElements.define("pdf-document", PdfDocumentCustomElement);
    }

    if (app.ports.sendPdfCommand) {
      app.ports.sendPdfCommand.subscribe(function (cmd) {
        loadPdfjs()
          .then(function (pdfjsLib) {
            pdfCommandHandler(app, cmd, pdfjsLib);
          })
          .catch(function (err) {
            if (app.ports.receivePdfMsg) {
              app.ports.receivePdfMsg.send({
                msg: "error",
                name: typeof cmd.name === "string" ? cmd.name : "",
                error: formatPdfError(err),
              });
            }
          });
      });
    }
  },
};

export default PdfPreviewPort;
